/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useAuthContext } from '@mpb-com/auth-sdk/client';
import { MpbLogo } from '@mpb-com/feather/components/atoms/mpb-logo';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import React, { FunctionComponent, ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { NavigationLinks } from '@toucan/common/services/navigation/types';
import { getConfig } from '@toucan/common/services/nextjs/settings';

import {
    ApplicationContext,
    MediaQuery,
    useMediaQuery,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';

import { SitewideBannerData } from '@toucan/client/core/contentful';

// eslint-disable-next-line boundaries/element-types -- This will be updated when we migrate how we do contentful blocks
import { contentfulSitewideBannerDataToSitewideBanner } from '@toucan/client/features/contentful';
import {
    getCheckoutFooterLinks,
    getDefaultFooterLinks,
    getRestrictedFooterLinks,
} from '@toucan/client/features/layout/helpers/footer-links';

import { TrustedShopsBadge } from '../../shared/components/trusted-shops-badge';
import { Footer, FooterVariant } from './components/footer';
import {
    RestrictedFooter,
    RestrictedFooterVariant,
} from './components/restricted-footer';
import { SitewideBanner } from './components/sitewide-banner';
import { StickyFooter } from './components/sticky-footer';
import { useConfirmMarketDialog } from './hooks/use-confirm-market-dialog';
import { useSessionStorage } from './hooks/use-session-storage';
import { styles } from './layout.styles';

export { AccountLayout } from './components/account-layout';
export { Footer, FooterVariant } from './components/footer';
export { Header } from './components/header';
export { PreFooter } from './components/pre-footer';
export { StickyFooter } from './components/sticky-footer';
export { TrustedShopsBadge } from '../../shared/components/trusted-shops-badge';
export {
    PaymentMethods,
    PaymentMethodsVariant,
} from '../../shared/components/payment-methods';
export { LinkList, LinkListSeparator } from '../../shared/components/link-list';
export { SitewideBannerColours } from './components/sitewide-banner';

// @ts-ignore
const CheckoutHeader = dynamic(() =>
    import('./components/checkout-header').then((mod) => mod.CheckoutHeader)
);

// @ts-ignore
const QuoteHeader = dynamic(() =>
    import('./components/quote-header').then((mod) => mod.QuoteHeader)
);

// @ts-ignore
const Header = dynamic(() =>
    import('./components/header').then((mod) => mod.Header)
);

// @ts-ignore
const PreFooter = dynamic(() =>
    import('./components/pre-footer').then((mod) => mod.PreFooter)
);

export enum LayoutType {
    FULL = 'full',
    ORIGINAL_CONTENT = 'original-content',
    MINIMAL = 'minimal',
    CHECKOUT = 'checkout',
    CHECKOUT_LOGIN = 'checkout-login',
    QUOTE = 'quote',
    ERROR404 = 'error-404',
    WITHDRAW_BALANCE = 'withdraw-balance',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface LayoutProps {
    /**
     * The type of layout to display.
     *
     * @default FULL
     */
    layoutType?: LayoutType;

    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Whether to show the right of withdrawal link in layout
     */
    showRightOfWithdrawal?: boolean;

    /**
     * Sitewide banners loaded server side from Contentful
     */
    sitewideBannerData?: SitewideBannerData[];
    navigationLinks?: NavigationLinks;
}

const StyledLayout = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Layout: FunctionComponent<LayoutProps> = ({
    layoutType = LayoutType.FULL,
    children,
    showRightOfWithdrawal = false,
    sitewideBannerData = [],
    navigationLinks = { originalContentLinks: [] },
    ...otherProps
}: LayoutProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { legalLinks } = useContext(ApplicationContext);
    const { isLoggedIn } = useAuthContext();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const isSmallMobile = useMediaQuery(MediaQuery.SMALL_MOBILE);
    const [bannersToHide, setBannersToHide] = useSessionStorage<string[]>(
        'sitewideBanners',
        []
    );

    const {
        publicRuntimeConfig: { trustedShopsId, marketsWithTrustedShops },
    } = getConfig();

    const displayTrustedShopsBadge = marketsWithTrustedShops?.includes(
        activeMarket.code
    );

    const links = getDefaultFooterLinks(t, activeMarket, legalLinks);

    /**
     * Update session storage and bannersToHide to remove banner
     */
    function handleCloseBanner(id: string) {
        setBannersToHide((ids) => {
            return [...ids, id];
        });
    }

    /**
     * Render the sitewide-banners to the UI
     */
    function renderSitewideBanners() {
        return sitewideBannerData?.map((data, index) => {
            const { content, colour, id } =
                contentfulSitewideBannerDataToSitewideBanner(data);

            if (bannersToHide.includes(id)) {
                return null;
            }

            return (
                <SitewideBanner
                    colour={colour}
                    key={index}
                    className="sitewide-banner"
                    onClose={() => handleCloseBanner(id)}
                >
                    {content}
                </SitewideBanner>
            );
        });
    }

    useConfirmMarketDialog(layoutType);

    switch (layoutType) {
        case LayoutType.MINIMAL:
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <header data-id="site-header">
                        <NextLink
                            prefetch={false}
                            passHref={true}
                            as={`/${activeMarket.slug}`}
                            href="/"
                            legacyBehavior
                            data-id="site-logo-link"
                        >
                            <MpbLogo className={'logo'} href="/" />
                        </NextLink>
                    </header>
                    {renderSitewideBanners()}
                    <main data-id="site-main">{children}</main>
                </StyledLayout>
            );

        case LayoutType.CHECKOUT: {
            const checkoutFooterLinks = getRestrictedFooterLinks(
                t,
                activeMarket,
                showRightOfWithdrawal,
                legalLinks
            );

            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <CheckoutHeader
                        data-testid="site-header"
                        data-id="site-header"
                    />
                    {renderSitewideBanners()}
                    {children}
                    <StickyFooter data-id="site-sticky-footer" />
                    <RestrictedFooter
                        linkList={checkoutFooterLinks}
                        variant={
                            isSmallTablet
                                ? RestrictedFooterVariant.MOBILE
                                : RestrictedFooterVariant.DESKTOP
                        }
                        data-testid="site-restricted-footer"
                        data-id="site-footer"
                    />
                </StyledLayout>
            );
        }

        case LayoutType.WITHDRAW_BALANCE: {
            const withdrawBalanceFooterLinks = getRestrictedFooterLinks(
                t,
                activeMarket,
                showRightOfWithdrawal,
                legalLinks
            );

            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <CheckoutHeader
                        data-testid="site-header"
                        data-id="site-header"
                        showPaymentMethods={false}
                        backLabel={t('Back to your account')}
                        backLinkDestination={`/${activeMarket.slug}/${t(
                            'account',
                            { ns: 'urls' }
                        )}/${t('balance', { ns: 'urls' })}`}
                        backLinkHref="/account/balance"
                    />
                    {renderSitewideBanners()}
                    {children}
                    <StickyFooter data-id="site-sticky-footer" />
                    <RestrictedFooter
                        linkList={withdrawBalanceFooterLinks}
                        variant={
                            isSmallTablet
                                ? RestrictedFooterVariant.MOBILE
                                : RestrictedFooterVariant.DESKTOP
                        }
                        data-testid="site-restricted-footer"
                        data-id="site-footer"
                    />
                </StyledLayout>
            );
        }

        case LayoutType.CHECKOUT_LOGIN: {
            const checkoutFooterLinks = getCheckoutFooterLinks(t, activeMarket);
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <CheckoutHeader
                        data-testid="site-header"
                        data-id="site-header"
                        backLabel={t('Back to basket')}
                        backLinkDestination={`/${activeMarket.slug}/s/${t(
                            'basket',
                            { ns: 'urls' }
                        )}`}
                        backLinkHref="/basket"
                    />
                    {renderSitewideBanners()}
                    {children}
                    <StickyFooter data-id="site-sticky-footer" />
                    {!isSmallTablet && (
                        <Footer
                            linkLists={checkoutFooterLinks}
                            variant={FooterVariant.DESKTOP}
                            data-testid="site-footer"
                            data-id="site-footer"
                        />
                    )}
                </StyledLayout>
            );
        }

        case LayoutType.QUOTE: {
            const checkoutFooterLinks = getRestrictedFooterLinks(
                t,
                activeMarket,
                showRightOfWithdrawal,
                legalLinks
            );
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <QuoteHeader
                        data-testid="site-header"
                        data-id="site-header"
                    />
                    {renderSitewideBanners()}
                    {children}
                    <StickyFooter data-id="site-sticky-footer" />
                    <RestrictedFooter
                        linkList={checkoutFooterLinks}
                        variant={
                            isSmallTablet
                                ? RestrictedFooterVariant.MOBILE
                                : RestrictedFooterVariant.DESKTOP
                        }
                        data-testid="site-restricted-footer"
                        data-id="site-footer"
                    />
                </StyledLayout>
            );
        }
        case LayoutType.ERROR404:
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <Header
                        data-testid="site-header"
                        loggedIn={isLoggedIn}
                        data-id="site-header"
                        navigationLinks={navigationLinks}
                    />
                    {renderSitewideBanners()}
                    {children}

                    {
                        <Footer
                            linkLists={links}
                            variant={
                                isMobile || isSmallTablet || isSmallMobile
                                    ? FooterVariant.MOBILE
                                    : FooterVariant.DESKTOP
                            }
                            data-testid="site-footer"
                            data-id="site-footer"
                        />
                    }

                    {isSmallTablet && displayTrustedShopsBadge && (
                        <TrustedShopsBadge
                            trustedShopsId={trustedShopsId}
                            id="trustedShopsBadge"
                            data-id="trusted-shops-badge"
                        />
                    )}
                </StyledLayout>
            );
        case LayoutType.ORIGINAL_CONTENT:
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <Header
                        data-testid="site-header"
                        loggedIn={isLoggedIn}
                        data-id="site-header"
                        navigationLinks={navigationLinks}
                    />
                    {renderSitewideBanners()}
                    {children}

                    <Footer
                        linkLists={links}
                        variant={FooterVariant.DESKTOP}
                        data-testid="site-footer"
                        className="site-footer"
                        data-id="site-footer"
                    />

                    {isSmallTablet && displayTrustedShopsBadge && (
                        <TrustedShopsBadge
                            trustedShopsId={trustedShopsId}
                            id="trustedShopsBadge"
                            data-id="trusted-shops-badge"
                        />
                    )}
                    <StickyFooter data-id="site-sticky-footer" />
                </StyledLayout>
            );
        case LayoutType.FULL:
        default:
            return (
                <StyledLayout
                    layoutType={layoutType}
                    data-id="site-wrapper"
                    {...otherProps}
                >
                    <Header
                        data-testid="site-header"
                        loggedIn={isLoggedIn}
                        data-id="site-header"
                        navigationLinks={navigationLinks}
                    />
                    {renderSitewideBanners()}
                    {children}
                    <PreFooter data-id="site-pre-footer" />

                    {!isSmallTablet && (
                        <Footer
                            linkLists={links}
                            variant={FooterVariant.DESKTOP}
                            data-testid="site-footer"
                            data-id="site-footer"
                        />
                    )}

                    {isSmallTablet && displayTrustedShopsBadge && (
                        <TrustedShopsBadge
                            trustedShopsId={trustedShopsId}
                            id="trustedShopsBadge"
                            data-id="trusted-shops-badge"
                        />
                    )}
                    <StickyFooter data-id="site-sticky-footer" />
                </StyledLayout>
            );
    }
};
